import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import LiveSignupCounter from './LiveSignupCounter';

function AudioWaveform() {
  const bars = 28;
  return (
    <div className="flex items-center gap-[3px] h-8 px-1">
      {Array.from({ length: bars }).map((_, i) => (
        <div
          key={i}
          className="rounded-full flex-1"
          style={{
            backgroundColor: i % 3 === 0 ? '#00FFD1' : i % 3 === 1 ? '#00A3E0' : '#00FFD180',
            animationName: 'waveBar',
            animationDuration: `${0.6 + (i % 7) * 0.12}s`,
            animationTimingFunction: 'ease-in-out',
            animationIterationCount: 'infinite',
            animationDirection: 'alternate',
            animationDelay: `${(i % 5) * 0.08}s`,
            height: '100%',
            minHeight: '4px',
          }}
        />
      ))}
      <style>{`
        @keyframes waveBar {
          0%   { transform: scaleY(0.15); opacity: 0.5; }
          100% { transform: scaleY(1);    opacity: 1;   }
        }
      `}</style>
    </div>
  );
}

const TRANSCRIPT_LINES = [
  { from: 'ATC', msg: 'N4521X, cleared to land runway 28L, wind 270 at 8.', highlight: true },
  { from: 'You', msg: 'Cleared to land runway 28L, N4521X.', highlight: false },
  { from: 'ATC', msg: 'Correct readback. Contact ground on 121.9 after landing.', highlight: false },
];

const CHAR_DELAY = 38;   // ms per character
const LINE_PAUSE = 900;  // ms pause between lines
const LOOP_PAUSE = 2200; // ms pause before restarting

function useTypingTranscript() {
  const [lineIndex, setLineIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [displayed, setDisplayed] = useState<string[]>(['', '', '']);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (done) {
      const t = setTimeout(() => {
        setDisplayed(['', '', '']);
        setLineIndex(0);
        setCharIndex(0);
        setDone(false);
      }, LOOP_PAUSE);
      return () => clearTimeout(t);
    }

    const currentMsg = TRANSCRIPT_LINES[lineIndex].msg;

    if (charIndex < currentMsg.length) {
      const t = setTimeout(() => {
        setDisplayed(prev => {
          const next = [...prev];
          next[lineIndex] = currentMsg.slice(0, charIndex + 1);
          return next;
        });
        setCharIndex(c => c + 1);
      }, CHAR_DELAY);
      return () => clearTimeout(t);
    }

    // Finished current line
    if (lineIndex < TRANSCRIPT_LINES.length - 1) {
      const t = setTimeout(() => {
        setLineIndex(l => l + 1);
        setCharIndex(0);
      }, LINE_PAUSE);
      return () => clearTimeout(t);
    }

    // All lines done
    const t = setTimeout(() => setDone(true), LINE_PAUSE);
    return () => clearTimeout(t);
  }, [lineIndex, charIndex, done]);

  return { displayed, lineIndex, done };
}

export default function HeroSection() {
  const navigate = useNavigate();
  const { displayed, lineIndex, done } = useTypingTranscript();

  return (
    <section className="relative min-h-screen flex items-center pt-20 pb-16 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0D1B2E] to-[#0A1628]">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'linear-gradient(#00FFD1 1px, transparent 1px), linear-gradient(90deg, #00FFD1 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6 sm:space-y-8">
            <div className="space-y-3 sm:space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight">
                WingmanFix
              </h1>
              <p className="text-xl sm:text-2xl lg:text-3xl text-[#00FFD1] font-medium">
                The copilot that keeps you ahead of the airplane.
              </p>
            </div>

            <div className="space-y-2 sm:space-y-3">
              <p className="text-lg sm:text-xl lg:text-2xl text-white font-semibold leading-snug">
                As easy as talking to the right seat.
              </p>
              <p className="text-base sm:text-lg text-gray-400 font-medium">
                No extra screens. No extra workload. Just ask.
              </p>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed max-w-xl">
                WingmanFix helps in the moment and turns every flight into a complete post-flight debrief.
              </p>
            </div>

            <div className="flex flex-wrap gap-3 sm:gap-4 pt-2 sm:pt-4">
              <button
                onClick={() => navigate('/waitlist')}
                className="bg-[#00A3E0] hover:bg-[#0088BB] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg transition-all font-semibold text-sm sm:text-base shadow-lg hover:shadow-[#00A3E0]/50 hover:scale-105 cursor-pointer whitespace-nowrap">
                Download the App
              </button>
              <button
                onClick={() => navigate('/features')}
                className="border-2 border-[#00FFD1] text-[#00FFD1] hover:bg-[#00FFD1]/10 px-6 sm:px-8 py-3 sm:py-4 rounded-lg transition-all font-semibold text-sm sm:text-base cursor-pointer whitespace-nowrap">
                See How It Works
              </button>
            </div>

            <div className="pt-2">
              <LiveSignupCounter />
            </div>
          </div>

          {/* Right — Never Miss a Clearance */}
          <div className="relative flex flex-col justify-center h-full">
            <div className="bg-[#0D1B2E] rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-2xl border border-[#00FFD1]/20 relative overflow-hidden space-y-6 sm:space-y-8">
              <div className="absolute inset-0 bg-gradient-to-br from-[#00A3E0]/10 to-[#00FFD1]/5 pointer-events-none rounded-2xl sm:rounded-3xl"></div>

              {/* Headline */}
              <div className="relative space-y-2 sm:space-y-3">
                <div className="inline-flex items-center gap-2 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full px-3 sm:px-4 py-1 sm:py-1.5">
                  <span className="w-2 h-2 rounded-full bg-[#00FFD1] animate-pulse inline-block"></span>
                  <span className="text-[#00FFD1] text-xs font-semibold uppercase tracking-widest">Live ATC Assist</span>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                  Never miss a<br />
                  <span className="text-[#00FFD1]">clearance</span> again.
                </h2>
                <p className="text-gray-400 text-sm sm:text-base leading-relaxed max-w-sm">
                  WingmanFix listens to every ATC transmission in real time and surfaces exactly what you need — before you even have to ask.
                </p>
              </div>

              {/* Feature bullets */}
              <div className="relative space-y-3 sm:space-y-4">
                {[
                  { icon: 'ri-mic-line', title: 'Real-time radio transcription', desc: 'Every word from ATC, captured and displayed instantly.', color: '#00FFD1' },
                  { icon: 'ri-alert-line', title: 'Missed clearance alerts', desc: 'Get a heads-up the moment something needs your attention.', color: '#00A3E0' },
                  { icon: 'ri-repeat-line', title: 'Instant readback assist', desc: 'Confirm your readback is correct every single time.', color: '#B4FF39' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3 sm:gap-4">
                    <div
                      className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl shrink-0"
                      style={{ backgroundColor: `${item.color}18`, border: `1px solid ${item.color}40` }}
                    >
                      <i className={`${item.icon} text-base sm:text-lg`} style={{ color: item.color }}></i>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-xs sm:text-sm">{item.title}</p>
                      <p className="text-gray-500 text-xs sm:text-sm leading-snug">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Live ATC transcript card */}
              <div className="relative bg-[#0A1628] rounded-xl sm:rounded-2xl p-4 sm:p-5 border border-gray-700 space-y-2 sm:space-y-3">
                {/* Header row */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse inline-block"></span>
                    <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">Live Transcript</span>
                  </div>
                  <div className="w-24 sm:w-32 h-6 sm:h-8">
                    <AudioWaveform />
                  </div>
                </div>

                {/* Typed lines */}
                <div className="space-y-2 min-h-[80px]">
                  {TRANSCRIPT_LINES.map((line, i) => {
                    const isVisible = i < lineIndex || (i === lineIndex) || done;
                    const isTyping = i === lineIndex && !done;
                    const text = displayed[i] ?? '';

                    if (!isVisible && !done) return null;

                    return (
                      <div
                        key={i}
                        className={`flex gap-2 text-xs sm:text-sm transition-opacity duration-300 ${
                          isVisible || done ? 'opacity-100' : 'opacity-0'
                        } ${line.highlight ? 'text-[#00FFD1]' : 'text-gray-400'}`}
                      >
                        <span className={`font-bold shrink-0 w-7 sm:w-8 ${line.highlight ? 'text-[#00FFD1]' : 'text-gray-500'}`}>
                          {line.from}
                        </span>
                        <span className="leading-snug font-mono">
                          {done ? line.msg : text}
                          {isTyping && (
                            <span
                              className="inline-block w-[2px] h-[1em] ml-[1px] align-middle"
                              style={{
                                backgroundColor: line.highlight ? '#00FFD1' : '#9ca3af',
                                animationName: 'blink',
                                animationDuration: '0.75s',
                                animationTimingFunction: 'step-end',
                                animationIterationCount: 'infinite',
                              }}
                            />
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <style>{`
                  @keyframes blink {
                    0%, 100% { opacity: 1; }
                    50%       { opacity: 0; }
                  }
                `}</style>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}